import { useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";
import { EditorialPage, useDoc, Loading } from "@/components/site/EditorialPage";
import { ArrowRight } from "lucide-react";

function NotFoundInline() {
  return <div className="mx-auto max-w-3xl px-6 py-32 text-center"><h1 className="font-serif text-4xl text-deep-navy">Page not found</h1><Link to="/capabilities" className="mt-6 inline-flex items-center gap-2 text-restrained-blue">Explore capabilities</Link></div>;
}

function BlueprintDoc() {
  const { pathname } = useLocation();
  const { data, nf } = useDoc(`/page${pathname}`, [pathname]);
  if (nf) return <NotFoundInline />;
  if (!data) return <Loading />;
  return <EditorialPage data={data} />;
}

export function BlueprintPage() { return <BlueprintDoc />; }

function Doc({ url, deps }) {
  const { data, nf } = useDoc(url, deps);
  if (nf) return <BlueprintDoc />;
  if (!data) return <Loading />;
  return <EditorialPage data={data} />;
}

export function PracticePage() { const { practice } = useParams(); return <Doc url={`/practice/${practice}`} deps={[practice]} />; }
export function CapabilityDetail({ prefix }) { const p = useParams(); const pre = prefix || p.prefix; return <Doc url={`/capability/${pre}/${p.slug}`} deps={[pre, p.slug]} />; }
export function IndustryPage() { const { industry } = useParams(); return <Doc url={`/industry/${industry}`} deps={[industry]} />; }
export function UseCaseDetail() { const { slug } = useParams(); return <Doc url={`/use-case/${slug}`} deps={[slug]} />; }
export function CountryPage() { const { country } = useParams(); return <Doc url={`/country/${country}`} deps={[country]} />; }
export function CountryTopicPage() { const { country, topic } = useParams(); return <Doc url={`/country/${country}/${topic}`} deps={[country, topic]} />; }

export function CapabilitiesLanding() {
  useSeo({ title: "Capabilities", description: "ThinkStack capabilities: Supply Chain, Marketing & Customer, Learning & Education and Enterprise AI & Workflow Automation — strategy to deployment, from Singapore.", path: "/capabilities" });
  const { data } = useDoc("/capabilities", []);
  return (
    <div>
      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-12 pt-16 pb-12 md:pt-20">
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.98] text-deep-navy max-w-4xl">Strategy, transformation and technology — delivered end to end.</h1>
          <p className="mt-8 text-xl text-charcoal/70 max-w-2xl">Four practices, one method: redesign the process, then build and deploy the AI that changes performance.</p>
        </div>
      </section>
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-14 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-6">
          {!data ? <Loading /> : data.practices.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.06}>
              <Link to={`/capabilities/${p.slug}`} data-testid={`practice-card-${p.slug}`} className="group block border border-[#E5E5E5] overflow-hidden h-full transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(10,25,47,0.22)]">
                <div className="aspect-[16/9] overflow-hidden relative">
                  <span className="absolute top-0 left-0 z-10 h-1.5 w-full" style={{ backgroundColor: p.accent }} />
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-8">
                  <p className="overline" style={{ color: p.accent }}>{p.name}</p>
                  <h2 className="font-serif text-2xl md:text-3xl text-deep-navy mt-2">{p.headline}</h2>
                  <p className="mt-3 text-charcoal/70">{p.proposition}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm group-hover:gap-3 transition-all" style={{ color: p.accent }}>Explore practice <ArrowRight size={15} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

export function IndustriesLanding() {
  useSeo({ title: "Industries", description: "Enterprise AI transformation by industry — retail, manufacturing, logistics, FMCG, healthcare, financial services and more, across Southeast Asia.", path: "/industries" });
  const { data } = useDoc("/industries", []);
  return (
    <div>
      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-12 pt-16 pb-12 md:pt-20">
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.98] text-deep-navy max-w-4xl">Depth where the process is the business.</h1>
          <p className="mt-8 text-xl text-charcoal/70 max-w-2xl">We bring an AI-native transformation approach to the operating realities of each sector.</p>
        </div>
      </section>
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-14 md:py-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {!data ? <Loading /> : data.industries.map((ind, i) => (
            <Reveal key={ind.slug} delay={i * 0.04}>
              <Link to={`/industries/${ind.slug}`} data-testid={`industry-card-${ind.slug}`} className="group block border border-[#E5E5E5] overflow-hidden hover:-translate-y-1 transition-all hover:shadow-[0_12px_40px_-12px_rgba(10,25,47,0.2)]">
                <div className="aspect-[16/10] overflow-hidden"><img src={ind.img} alt={ind.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="p-6 flex items-center justify-between"><span className="font-serif text-xl text-deep-navy">{ind.name}</span><ArrowRight size={16} className="text-restrained-blue transition-transform group-hover:translate-x-1" /></div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

export function UseCasesLanding() {
  useSeo({ title: "Use Cases", description: "A library of enterprise AI use cases across supply chain, marketing, finance, operations and learning — with the redesigned workflow behind each.", path: "/use-cases" });
  const { data } = useDoc("/use-cases", []);
  const [filters, setFilters] = useState({ function: "", industry: "", technology: "", outcome: "" });

  const set = (dim) => (val) => setFilters((f) => ({ ...f, [dim]: f[dim] === val ? "" : val }));
  const clearAll = () => setFilters({ function: "", industry: "", technology: "", outcome: "" });

  const filtered = (data?.use_cases || []).filter((u) =>
    (!filters.function || u.function === filters.function) &&
    (!filters.industry || u.industry === filters.industry) &&
    (!filters.technology || u.tech === filters.technology) &&
    (!filters.outcome || u.outcome === filters.outcome)
  );
  const activeCount = Object.values(filters).filter(Boolean).length;

  const FilterGroup = ({ label, dim, options }) => (
    <div>
      <p className="overline text-charcoal/50 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = filters[dim] === o;
          return (
            <button key={o} data-testid={`usecase-filter-${dim}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => set(dim)(o)}
              className={`px-3 py-1.5 text-xs border transition-colors ${active ? "bg-deep-navy text-white border-deep-navy" : "bg-white text-charcoal/70 border-[#E5E5E5] hover:border-deep-navy"}`}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-12 pt-16 pb-12 md:pt-20">
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.98] text-deep-navy max-w-4xl">Use Cases</h1>
          <p className="mt-8 text-xl text-charcoal/70 max-w-2xl">Concrete decisions we redesign with AI — the problem, the redesigned workflow, and where humans stay in the loop.</p>
        </div>
      </section>
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-14 md:py-20">
          {!data ? <Loading /> : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <aside className="lg:col-span-3 space-y-7" data-testid="usecase-filters">
                <div className="flex items-center justify-between">
                  <p className="font-serif text-xl text-deep-navy">Filter</p>
                  {activeCount > 0 && (
                    <button onClick={clearAll} data-testid="usecase-filter-clear" className="text-xs text-restrained-blue hover:underline">Clear all</button>
                  )}
                </div>
                <FilterGroup label="Function" dim="function" options={data.functions} />
                <FilterGroup label="Industry" dim="industry" options={data.industries} />
                <FilterGroup label="Technology" dim="technology" options={data.technologies} />
                <FilterGroup label="Business outcome" dim="outcome" options={data.outcomes} />
              </aside>

              <div className="lg:col-span-9">
                <p className="text-sm text-charcoal/70 mb-4" data-testid="usecase-result-count">{filtered.length} use case{filtered.length === 1 ? "" : "s"}</p>
                {filtered.length === 0 ? (
                  <div className="border border-[#E5E5E5] p-12 text-center text-charcoal/60">No use cases match these filters. <button onClick={clearAll} className="text-restrained-blue hover:underline">Reset</button></div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
                    {filtered.map((u, i) => (
                      <Reveal key={u.slug} delay={Math.min(i, 6) * 0.03}>
                        <Link to={`/use-cases/${u.slug}`} data-testid={`usecase-card-${u.slug}`} className="group block bg-white p-7 h-full hover:bg-off-white transition-colors">
                          <p className="overline" style={{ color: "#5C2D50" }}>{u.function} · {u.industry}</p>
                          <h3 className="font-serif text-2xl text-deep-navy mt-2">{u.title}</h3>
                          <p className="mt-2 text-sm text-charcoal/60">Outcome: {u.outcome}</p>
                          <span className="mt-4 inline-flex items-center gap-1 text-sm text-deep-navy group-hover:gap-2 transition-all">View use case <ArrowRight size={14} /></span>
                        </Link>
                      </Reveal>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
